import cookie from "js-cookie";
import qs from "query-string";

export type ResultSubmission = {
	sub: Submission;
	author: UserBrief;
	problem?: Problem;
};

export type SubmissionQuery = {
	user_id?: number;
	problem_id?: number;
	problem_list_id?: number;
	contest_id?: number;
	score?: number;
	status?: string;
	lang?: string;

	page: number;

	compile_error?: boolean;
	ordering?: string;
	ascending?: boolean;

	limit?: number;
};

type RequestParams = {
	url: string;
	method: "GET" | "POST" | "PUT" | "DELETE";
	queryParams?: any;
	body?: BodyInit;
	headers?: HeadersInit;
	signal?: AbortSignal;
};

export type Submissions = {
	submissions: Submission[];
	count: number;
	truncated_count: boolean;
	users: Record<string, UserBrief>;
	problems: Record<string, Problem>;
};

export type Response<T> = { status: "error"; data: string; statusCode: number } | { status: "success"; data: T; statusCode: number };

export class KNClient {
	session: string;

	constructor(session: string, base: string) {
		this.session = session;
	}

	setSession(newSession: string) {
		this.session = newSession;
	}

	async request<T = any>(params: RequestParams): Promise<Response<T>> {
		if (params.url.startsWith("/")) {
			params.url = params.url.substring(1);
		}
		try {
			let resp = await fetch(`${window.platform_info.apiPrefix ?? "/api"}/${params.url}?${qs.stringify(params.queryParams)}`, {
				method: params.method,
				headers: {
					Accept: "application/json",
					Authorization: this.session,
					...params.headers,
				},
				body: params.body || null,
				signal: params.signal,
			});
			let data = (await resp.json()) as Response<T>;
			data.statusCode = resp.status;
			return data;
		} catch (e: any) {
			return { status: "error", data: e.toString(), statusCode: 999 };
		}
	}

	async requestV2<T = any>(params: RequestParams): Promise<Response<T>> {
		if (params.url.startsWith("/")) {
			params.url = params.url.substring(1);
		}
		try {
			let resp = await fetch(`${window.platform_info.apiPrefix ?? "/api"}/${params.url}?${qs.stringify(params.queryParams)}`, {
				method: params.method,
				headers: {
					Accept: "application/json",
					Authorization: this.session,
					...params.headers,
				},
				body: params.body || null,
				signal: params.signal,
			});
			if(resp.ok) {
				return ({
					data: (await resp.json()) as T,
					status: "success",
					statusCode: resp.status,
				}) as Response<T>;
			}
			return ({
				data: (await resp.json()).errors.map(val => val.message).join("; "),
				status: "error",
				statusCode: resp.status,
			}) as Response<T>;
		} catch (e: any) {
			return { status: "error", data: e.toString(), statusCode: 999 };
		}
	}

	async getUser(id: number) {
		let res = await this.request<UserBrief>({
			url: `/user/byID/${id}`,
			method: "GET",
			queryParams: {},
		});
		if (res.status === "error") {
			throw new Error(res.data);
		}
		return res.data;
	}
}

export const defaultClient = new KNClient(cookie.get("kn-sessionid") || "guest", "/api");

export async function getCall<T = any>(call: string, params: any, signal?: AbortSignal): Promise<Response<T>> {
	return defaultClient.request<T>({
		url: call,
		method: "GET",
		queryParams: params,
		signal: signal,
	});
}

export async function postCall<T = any>(call: string, params: any): Promise<Response<T>> {
	return defaultClient.request<T>({
		url: call,
		method: "POST",
		headers: {
			"Content-Type": "application/x-www-form-urlencoded",
		},
		body: qs.stringify(params),
	});
}

export async function bodyCall<T = any>(call: string, body: any): Promise<Response<T>> {
	return defaultClient.request<T>({
		url: call,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(body),
	});
}

export async function postCallV2<T = any>(call: string, body: any): Promise<Response<T>> {
	return defaultClient.requestV2<T>({
		url: call,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(body),
	});
}

export async function multipartCall<T = any>(call: string, formdata: FormData): Promise<Response<T>> {
	return defaultClient.request<T>({
		url: call,
		method: "POST",
		body: formdata,
	});
}
